import { SxProps } from "@mui/system";
import { ThemeV2 } from "../../util/types";
import { questionCardSurface } from "../../components/ui/QuestionSurface/styles";

export const conjointContainer = (
  theme: ThemeV2,
  isWideLayout: boolean,
): SxProps => ({
  width: "100%",
  maxWidth: isWideLayout ? "none" : "760px",
  margin: "0 auto",
  padding: isWideLayout
    ? theme.tokens.spacing["3"] / 8
    : theme.tokens.spacing["2"] / 8,
  color: theme.tokens.color.text.primary,
  fontFamily: theme.tokens.typography.fontFamily,
  boxSizing: "border-box",
});

export const questionContainer: SxProps = {
  width: "100%",
  marginBottom: 1.5,
};

export const landscapeScrollContainer = (theme: ThemeV2): SxProps => ({
  width: "100%",
  overflowX: "auto",
  paddingBottom: 0.5,
  scrollbarColor: `${theme.tokens.color.border.strong} transparent`,
});

export const landscapeTable = (
  optionCount: number,
  theme: ThemeV2,
): SxProps => ({
  width: "100%",
  minWidth: `${Math.max(590, optionCount * 190 + 220)}px`,
  borderCollapse: "separate",
  borderSpacing: `${theme.tokens.spacing["3"]}px 0`,
  tableLayout: "fixed",
});

export const landscapeConceptHeader = (theme: ThemeV2): SxProps => ({
  padding: `${theme.tokens.spacing["2"]}px`,
  backgroundColor: theme.tokens.color.action.primary,
  color: theme.tokens.color.action.primaryText,
  fontSize: {
    md: theme.tokens.typography.size.sm,
    xs: theme.tokens.typography.size.xs,
  },
  fontWeight: theme.tokens.typography.weight.bold,
  letterSpacing: "0.12em",
  textTransform: "uppercase",
  textAlign: "center",
  overflowWrap: "anywhere",
});

export const landscapeRowHeader = (theme: ThemeV2): SxProps => ({
  width: "clamp(160px, 18vw, 220px)",
  padding: `${theme.tokens.spacing["3"]}px ${theme.tokens.spacing["3"]}px ${theme.tokens.spacing["3"]}px 0`,
  textAlign: "right",
  verticalAlign: "middle",
  fontWeight: theme.tokens.typography.weight.bold,
  letterSpacing: "0.1em",
  textTransform: "uppercase",
  overflowWrap: "normal",
  wordBreak: "normal",
});

export const landscapeCell = (theme: ThemeV2): SxProps => ({
  padding: `${theme.tokens.spacing["3"]}px`,
  borderLeft: `1px solid ${theme.tokens.color.border.default}`,
  borderRight: `1px solid ${theme.tokens.color.border.default}`,
  borderBottom: `1px solid ${theme.tokens.color.border.default}`,
  backgroundColor: theme.tokens.color.surface.card,
  color: theme.tokens.color.text.primary,
  fontSize: {
    md: theme.tokens.typography.size.md,
    xs: theme.tokens.typography.size.xs,
  },
  lineHeight: theme.tokens.typography.lineHeight.normal,
  verticalAlign: "middle",
  overflowWrap: "anywhere",
  wordBreak: "break-word",
  "tfoot &": {
    borderBottomLeftRadius: theme.tokens.shape.radius.md,
    borderBottomRightRadius: theme.tokens.shape.radius.md,
  },
});

export const cardMedia: SxProps = {
  width: "100%",
  padding: 1.5,
  minHeight: "180px",
  maxHeight: "180px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
};

export const portraitCards: SxProps = {
  width: "100%",
  display: "flex",
  flexDirection: "column",
  gap: 1.25,
};

export const card = (theme: ThemeV2): SxProps => ({
  width: "100%",
  ...questionCardSurface(theme),
  overflow: "hidden",
  boxSizing: "border-box",
});

export const cardAttribute = (theme: ThemeV2): SxProps => ({
  paddingY: `${theme.tokens.spacing["1"]}px`,
  paddingX: `${theme.tokens.spacing["3"]}px`,
  borderBottom: `1px solid ${theme.tokens.color.border.default}`,
});

export const cardAttributeLabel = (theme: ThemeV2): SxProps => ({
  color: theme.tokens.color.text.secondary,
  fontSize: {
    md: theme.tokens.typography.size.sm,
    xs: theme.tokens.typography.size.xs,
  },
  fontWeight: theme.tokens.typography.weight.bold,
  letterSpacing: "0.1em",
  textTransform: "uppercase",
  overflowWrap: "anywhere",
});

export const actionButtonWrapper = (theme: ThemeV2): SxProps => ({
  width: "100%",
  "& > button": {
    width: "100%",
    minHeight: "36px",
    margin: 0,
    boxShadow: "none",
    fontSize: theme.tokens.typography.size,
    fontWeight: theme.tokens.typography.weight.medium,
  },
  "& > button:hover": {
    boxShadow: "none",
  },
});

export const fixedOptionsContainer: SxProps = {
  width: "100%",
  display: "flex",
  flexDirection: "column",
  gap: 1,
  marginTop: 1.25,
};
